import React, { useRef, useEffect } from "react";
import { toast } from "react-toastify";
import Image from "src/shared/Image";
import { X } from "lucide-react"; // optional icon (recommended)

interface ImageUploaderProps {
  value: File | null;
  previewImage?: string | null;
  onChange: (file: File | null) => void;
  maxSizeMB?: number;
}

const ImageUploader: React.FC<ImageUploaderProps> = ({ value, previewImage = null, onChange, maxSizeMB = 5 }) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const MAX_FILE_SIZE = maxSizeMB * 1024 * 1024;

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please upload a valid image file");
      e.target.value = "";
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      toast.error(`Image must be under ${maxSizeMB}MB`);
      e.target.value = "";
      return;
    }

    onChange(file);
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation(); // ⛔ prevent opening file picker
    onChange(null);

    if (fileRef.current) {
      fileRef.current.value = "";
    }
  };

  const previewUrl = value instanceof File ? URL.createObjectURL(value) : previewImage;

  useEffect(() => {
    return () => {
      if (value instanceof File && previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [value, previewUrl]);

  return (
    <div
      onClick={() => fileRef.current?.click()}
      className="relative cursor-pointer border border-gray-300
                 w-[139px] h-[139px] rounded-lg overflow-hidden
                 flex items-center justify-center bg-white hover:border-gray-400"
    >
      {/* ❌ Remove Button */}
      {previewUrl && (
        <button
          onClick={handleRemove}
          className="absolute top-1 right-1 z-10
                     bg-black/60 hover:bg-black
                     text-white rounded-full p-1"
        >
          <X size={14} />
        </button>
      )}

      {/* Image / Placeholder */}
      {previewUrl ? (
        previewUrl.startsWith("blob:") || previewUrl.startsWith("data:") ? (
          <img src={previewUrl} alt="Preview" className="w-full h-full object-cover" />
        ) : (
          <Image imagePath={previewUrl} />
        )
      ) : (
        <span className="text-xs text-gray-400 text-center px-2">Upload Image (under {maxSizeMB}MB)</span>
      )}

      <input ref={fileRef} type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
    </div>
  );
};

export default ImageUploader;
